import { useState, useEffect } from "react";
import { Car, Plus, Trash2, Edit2, Search, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import VehicleSelector from "../components/VehicleSelector";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  
  const [formData, setFormData] = useState({
    customer_id: "",
    brand: "",
    model: "",
    version: "",
    year: "",
    vin: "",
    plate: "",
    engine_code: ""
  });

  useEffect(() => {
    fetchVehicles();
    fetchCustomers();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await authFetch(`${API}/customer-vehicles`, {
      });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento veicoli");
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const response = await authFetch(`${API}/customers`, {
      });
      if (response.ok) {
        setCustomers(await response.json());
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      brand: "",
      model: "",
      version: "",
      year: "",
      vin: "",
      plate: "",
      engine_code: ""
    });
    setEditingVehicle(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error("Seleziona un cliente");
      return;
    }
    
    if (!formData.plate) {
      toast.error("La targa è obbligatoria");
      return;
    }

    try {
      const url = editingVehicle 
        ? `${API}/customer-vehicles/${editingVehicle.vehicle_id}`
        : `${API}/customer-vehicles`;
      
      const response = await authFetch(url, {
        method: editingVehicle ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          year: parseInt(formData.year) || 0
        })
      });

      if (response.ok) {
        toast.success(editingVehicle ? "Veicolo aggiornato" : "Veicolo creato");
        fetchVehicles();
        setShowDialog(false);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      customer_id: vehicle.customer_id,
      brand: vehicle.brand,
      model: vehicle.model,
      version: vehicle.version || "",
      year: vehicle.year?.toString() || "",
      vin: vehicle.vin || "",
      plate: vehicle.plate,
      engine_code: vehicle.engine_code || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Eliminare questo veicolo?")) return;
    
    try {
      const response = await authFetch(`${API}/customer-vehicles/${vehicleId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        toast.success("Veicolo eliminato");
        fetchVehicles();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = searchTerm === "" || 
      v.plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCustomer = filterCustomer === "" || v.customer_id === filterCustomer;
    
    return matchesSearch && matchesCustomer;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="page-title">ARCHIVIO VEICOLI</h1>
          <p className="text-zinc-500 text-sm">{vehicles.length} veicoli registrati</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowDialog(true); }}
          className="btn-primary"
          data-testid="new-vehicle-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Veicolo
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Cerca per targa, marca, modello, cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
          />
        </div>
        <Select value={filterCustomer || "all"} onValueChange={(val) => setFilterCustomer(val === "all" ? "" : val)}>
          <SelectTrigger className="w-[250px] bg-zinc-900 border-zinc-800">
            <SelectValue placeholder="Filtra per cliente" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tutti i clienti</SelectItem>
            {customers.map(c => (
              <SelectItem key={c.customer_id} value={c.customer_id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vehicles Table */}
      {filteredVehicles.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500 uppercase">
                <th className="py-3 px-4 font-medium">Targa</th>
                <th className="py-3 px-4 font-medium">Marca/Modello</th>
                <th className="py-3 px-4 font-medium">Anno</th>
                <th className="py-3 px-4 font-medium">VIN</th>
                <th className="py-3 px-4 font-medium">Cliente</th>
                <th className="py-3 px-4 font-medium text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map(vehicle => (
                <tr 
                  key={vehicle.vehicle_id}
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <span className="font-mono text-lg font-bold text-primary">{vehicle.plate}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-white font-medium">
                      {vehicle.brand} {vehicle.model}
                    </span>
                    {vehicle.version && (
                      <span className="text-zinc-500 text-sm ml-1">{vehicle.version}</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-400">{vehicle.year > 0 ? vehicle.year : '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-500 font-mono text-xs">{vehicle.vin || '-'}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-zinc-300">{vehicle.customer_name}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(vehicle)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-primary"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(vehicle.vehicle_id)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-zinc-500">
          <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nessun veicolo trovato</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Car className="w-5 h-5 text-primary" />
              {editingVehicle ? "Modifica Veicolo" : "Nuovo Veicolo"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Customer Selection */}
            <div className="form-group">
              <label className="form-label">Cliente *</label>
              <Select 
                value={formData.customer_id} 
                onValueChange={(value) => setFormData(prev => ({...prev, customer_id: value}))}
                required
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {customers.map(c => (
                    <SelectItem 
                      key={c.customer_id} 
                      value={c.customer_id}
                      className="text-white hover:bg-zinc-800"
                    >
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vehicle Selector (Marca, Modello, Anno) */}
            <VehicleSelector
              selectedBrand={formData.brand}
              selectedModel={formData.model}
              selectedYear={formData.year}
              onBrandChange={(value) => setFormData(prev => ({...prev, brand: value}))}
              onModelChange={(value) => setFormData(prev => ({...prev, model: value}))}
              onYearChange={(value) => setFormData(prev => ({...prev, year: value}))}
              required
            />

            {/* Additional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Versione/Allestimento</label>
                <Input
                  className="bg-white border-zinc-300 text-zinc-900"
                  placeholder="es. Sport, Elegance"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({...prev, version: e.target.value}))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Targa *</label>
                <Input
                  className="bg-white border-zinc-300 text-zinc-900 uppercase font-mono"
                  placeholder="AA123BB"
                  value={formData.plate}
                  onChange={(e) => setFormData(prev => ({...prev, plate: e.target.value.toUpperCase()}))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">VIN / Telaio</label>
                <Input
                  className="bg-white border-zinc-300 text-zinc-900 uppercase font-mono text-xs"
                  placeholder="WVWZZZ3CZWE123456"
                  value={formData.vin}
                  onChange={(e) => setFormData(prev => ({...prev, vin: e.target.value.toUpperCase()}))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Codice Motore</label>
                <Input
                  className="bg-white border-zinc-300 text-zinc-900 uppercase font-mono"
                  placeholder="es. 169A4000"
                  value={formData.engine_code}
                  onChange={(e) => setFormData(prev => ({...prev, engine_code: e.target.value.toUpperCase()}))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-zinc-700"
              >
                Annulla
              </Button>
              <Button type="submit" className="btn-primary">
                {editingVehicle ? "Salva Modifiche" : "Crea Veicolo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
